import { SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { Button } from "../../../../components/ui/common/button";
import { Checkbox } from "../../../../components/ui/common/checkbox";
import { Label } from "../../../../components/ui/common/label";
import { Popover, PopoverContent, PopoverTrigger } from "../../../../components/ui/common/popover";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/common/radio-group";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from "../../../../components/ui/common/select";
import { supplierDisplayOnlyOptions, supplierOrderByOptions } from "../../../../config/constants";
import { SUPPLIER_TYPE_OPTIONS, useSupplierFilterState } from "../hooks/useSupplierFilterState";

interface SupplierFilterPanelProps {
  refetchWithFilters: (isSupplierAsUser: boolean | undefined, supplierTypes: number[], orderBy: string) => void;
}

/**
 * Tailwind replacement for the legacy Semantic UI `SupplierFilter` popup
 * (src/screens/supplier/supplierList/SupplierFilter.tsx) — same Order By /
 * Display Only / Supplier Data Type fields, rebuilt on the common
 * `Popover`/`Select`/`Checkbox` primitives, styled after `ProductFilterPanel`.
 * Supplier Data Type is only ever two values (In-House/External) so a small
 * checkbox list replaces the legacy multi-select dropdown — same resulting
 * array of selected type values.
 */
const SupplierFilterPanel = ({ refetchWithFilters }: SupplierFilterPanelProps) => {
  const [open, setOpen] = useState(false);
  const filterState = useSupplierFilterState(refetchWithFilters);

  const toggleType = (value: string) => {
    filterState.setSupplierTypes(
      filterState.supplierTypes.includes(value)
        ? filterState.supplierTypes.filter(v => v !== value)
        : [...filterState.supplierTypes, value]
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="outline" className="tw-shrink-0 tw-gap-2">
          <SlidersHorizontal size={16} />
          Filters
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="tw-w-72">
        <div className="tw-flex tw-flex-col tw-gap-4">
          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-text-sm tw-font-medium tw-text-foreground">Order By</p>
            <Select value={filterState.orderBy} onValueChange={filterState.setOrderBy}>
              <SelectTrigger className="!tw-h-9 tw-w-full tw-text-sm">
                <SelectValue placeholder="Select Order" />
              </SelectTrigger>
              <SelectContent>
                {supplierOrderByOptions.map((option: any) => (
                  <SelectItem key={option.key} value={option.value}>
                    {option.text}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-text-sm tw-font-medium tw-text-foreground">Display Only</p>
            <RadioGroup
              value={filterState.displayOnly}
              onValueChange={filterState.setDisplayOnly}
              className="tw-flex tw-flex-col tw-gap-2"
            >
              {supplierDisplayOnlyOptions.map((option: any) => (
                <label
                  key={option.value}
                  className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-foreground"
                >
                  <RadioGroupItem value={option.value} />
                  {option.text}
                </label>
              ))}
            </RadioGroup>
          </div>

          <div className="tw-flex tw-flex-col tw-gap-2">
            <p className="tw-text-sm tw-font-medium tw-text-foreground">Supplier Data Type</p>
            {SUPPLIER_TYPE_OPTIONS.map(option => (
              <label
                key={option.value}
                className="tw-flex tw-cursor-pointer tw-items-center tw-gap-2 tw-text-sm tw-text-foreground"
              >
                <Checkbox
                  checked={filterState.supplierTypes.includes(option.value)}
                  onCheckedChange={() => toggleType(option.value)}
                />
                <Label className="tw-cursor-pointer tw-text-sm tw-font-normal">{option.label}</Label>
              </label>
            ))}
          </div>

          <div className="tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-border tw-pt-3">
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                filterState.reset();
                setOpen(false);
              }}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={() => {
                filterState.apply();
                setOpen(false);
              }}
            >
              Apply
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default SupplierFilterPanel;
